/**
 * Keyword aliases map everyday language to pharmaceutical categories and example products.
 * 
 * This is a client-side placeholder. In production, this should come from a backend
 * endpoint (e.g., GET /search/suggestions?q=sleeping) that maps keywords to real
 * tradenames in the database.
 * 
 * Structure:
 * - keyword: the everyday term users might search for
 * - aliases: alternative spellings or related terms that should also match
 * - category: the pharmaceutical category name
 * - description: plain-language explanation of what these medicines do
 * - examples: sample trade names (for UX demonstration; real data comes from API)
 */

const KEYWORD_ALIASES = [
  {
    keyword: "sleeping pill",
    aliases: ["sleep aid", "insomnia", "can't sleep", "sleep medicine", "sleeping tablet"],
    category: "Hypnotics & Sedatives",
    description: "Medicines that help you fall asleep or stay asleep",
    examples: [
      { name: "Ambien", generic: "Zolpidem" },
      { name: "Lunesta", generic: "Eszopiclone" },
      { name: "Unisom", generic: "Doxylamine" },
      { name: "Sonata", generic: "Zaleplon" },
    ],
  },
  {
    keyword: "blood pressure",
    aliases: ["high blood pressure", "hypertension", "bp medicine", "bp pill"],
    category: "Antihypertensives",
    description: "Medicines that lower high blood pressure",
    examples: [
      { name: "Lisinopril", generic: "Lisinopril" },
      { name: "Norvasc", generic: "Amlodipine" },
      { name: "Losartan", generic: "Losartan" },
      { name: "Metoprolol", generic: "Metoprolol" },
    ],
  },
  {
    keyword: "pain killer",
    aliases: ["painkiller", "pain relief", "headache", "body pain", "pain medicine", "pain pill"],
    category: "Analgesics",
    description: "Medicines that relieve pain",
    examples: [
      { name: "Tylenol", generic: "Acetaminophen" },
      { name: "Advil", generic: "Ibuprofen" },
      { name: "Aleve", generic: "Naproxen" },
      { name: "Aspirin", generic: "Acetylsalicylic acid" },
    ],
  },
  {
    keyword: "cholesterol",
    aliases: ["high cholesterol", "cholesterol medicine", "statin", "lipid"],
    category: "Statins / Lipid-lowering",
    description: "Medicines that lower cholesterol levels",
    examples: [
      { name: "Lipitor", generic: "Atorvastatin" },
      { name: "Crestor", generic: "Rosuvastatin" },
      { name: "Zocor", generic: "Simvastatin" },
      { name: "Pravachol", generic: "Pravastatin" },
    ],
  },
  {
    keyword: "diabetes",
    aliases: ["blood sugar", "sugar medicine", "diabetic", "insulin", "type 2 diabetes"],
    category: "Antidiabetics",
    description: "Medicines that help control blood sugar levels",
    examples: [
      { name: "Metformin", generic: "Metformin" },
      { name: "Januvia", generic: "Sitagliptin" },
      { name: "Glucophage", generic: "Metformin" },
      { name: "Ozempic", generic: "Semaglutide" },
    ],
  },
  {
    keyword: "allergy",
    aliases: ["antihistamine", "hay fever", "allergic", "runny nose", "sneezing", "itchy eyes"],
    category: "Antihistamines",
    description: "Medicines that relieve allergy symptoms",
    examples: [
      { name: "Zyrtec", generic: "Cetirizine" },
      { name: "Claritin", generic: "Loratadine" },
      { name: "Allegra", generic: "Fexofenadine" },
      { name: "Benadryl", generic: "Diphenhydramine" },
    ],
  },
  {
    keyword: "anxiety",
    aliases: ["anxious", "nervous", "panic", "worry", "anxiety medicine", "calm down"],
    category: "Anxiolytics",
    description: "Medicines that help reduce anxiety and nervousness",
    examples: [
      { name: "Xanax", generic: "Alprazolam" },
      { name: "Ativan", generic: "Lorazepam" },
      { name: "Buspar", generic: "Buspirone" },
      { name: "Valium", generic: "Diazepam" },
    ],
  },
  {
    keyword: "depression",
    aliases: ["antidepressant", "depressed", "sad", "low mood", "depression medicine"],
    category: "Antidepressants",
    description: "Medicines that help treat depression",
    examples: [
      { name: "Zoloft", generic: "Sertraline" },
      { name: "Prozac", generic: "Fluoxetine" },
      { name: "Lexapro", generic: "Escitalopram" },
      { name: "Wellbutrin", generic: "Bupropion" },
    ],
  },
  {
    keyword: "acid reflux",
    aliases: ["heartburn", "stomach acid", "gerd", "indigestion", "antacid"],
    category: "Proton Pump Inhibitors / Antacids",
    description: "Medicines that reduce stomach acid",
    examples: [
      { name: "Prilosec", generic: "Omeprazole" },
      { name: "Nexium", generic: "Esomeprazole" },
      { name: "Pepcid", generic: "Famotidine" },
      { name: "Zantac", generic: "Ranitidine" },
    ],
  },
  {
    keyword: "erectile dysfunction",
    aliases: ["ed pill", "impotence", "viagra", "erection", "ed medicine"],
    category: "PDE5 Inhibitors",
    description: "Medicines that treat erectile dysfunction",
    examples: [
      { name: "Viagra", generic: "Sildenafil" },
      { name: "Cialis", generic: "Tadalafil" },
      { name: "Levitra", generic: "Vardenafil" },
      { name: "Stendra", generic: "Avanafil" },
    ],
  },
  {
    keyword: "thyroid",
    aliases: ["hypothyroid", "thyroid medicine", "underactive thyroid", "thyroid pill"],
    category: "Thyroid Hormones",
    description: "Medicines that treat underactive thyroid",
    examples: [
      { name: "Synthroid", generic: "Levothyroxine" },
      { name: "Armour Thyroid", generic: "Thyroid extract" },
      { name: "Levoxyl", generic: "Levothyroxine" },
      { name: "Cytomel", generic: "Liothyronine" },
    ],
  },
  {
    keyword: "antibiotic",
    aliases: ["infection", "bacteria", "antibacterial", "antibiotic pill"],
    category: "Antibiotics",
    description: "Medicines that fight bacterial infections",
    examples: [
      { name: "Amoxicillin", generic: "Amoxicillin" },
      { name: "Augmentin", generic: "Amoxicillin/Clavulanate" },
      { name: "Zithromax", generic: "Azithromycin" },
      { name: "Cipro", generic: "Ciprofloxacin" },
    ],
  },
  {
    keyword: "blood thinner",
    aliases: ["anticoagulant", "clot prevention", "blood clot", "thin blood"],
    category: "Anticoagulants",
    description: "Medicines that prevent blood clots",
    examples: [
      { name: "Eliquis", generic: "Apixaban" },
      { name: "Xarelto", generic: "Rivaroxaban" },
      { name: "Warfarin", generic: "Warfarin" },
      { name: "Plavix", generic: "Clopidogrel" },
    ],
  },
  {
    keyword: "asthma",
    aliases: ["inhaler", "breathing", "wheezing", "bronchodilator", "shortness of breath"],
    category: "Bronchodilators / Respiratory",
    description: "Medicines that help with breathing and asthma",
    examples: [
      { name: "Ventolin", generic: "Albuterol" },
      { name: "Advair", generic: "Fluticasone/Salmeterol" },
      { name: "Symbicort", generic: "Budesonide/Formoterol" },
      { name: "ProAir", generic: "Albuterol" },
    ],
  },
];

export default KEYWORD_ALIASES;

/**
 * Search keyword aliases by input text.
 * Returns matching alias groups sorted by relevance.
 */
export function searchKeywordAliases(query) {
  if (!query || query.length < 2) return [];

  const normalizedQuery = query.toLowerCase().trim();

  const matches = KEYWORD_ALIASES.filter((entry) => {
    // Check primary keyword
    if (entry.keyword.toLowerCase().includes(normalizedQuery)) return true;

    // Check aliases
    if (entry.aliases.some((alias) => alias.toLowerCase().includes(normalizedQuery)))
      return true;

    // Check example names
    if (
      entry.examples.some(
        (ex) =>
          ex.name.toLowerCase().includes(normalizedQuery) ||
          ex.generic.toLowerCase().includes(normalizedQuery)
      )
    )
      return true;

    return false;
  });

  // Sort: exact keyword match first, then alias match, then example match
  return matches
    .sort((a, b) => {
      const aExact = a.keyword.toLowerCase().startsWith(normalizedQuery) ? 0 : 1;
      const bExact = b.keyword.toLowerCase().startsWith(normalizedQuery) ? 0 : 1;
      return aExact - bExact;
    })
    .slice(0, 2); // Max 2 category groups in dropdown
}
